/* Browser Web Speech fallback.
 *
 * Only used when MP3 files haven't been generated yet (i.e. before the
 * user has run `npm run gen:demo-audio`). Less robotic-sounding than v1
 * thanks to neural-voice selection and bilingual chunking for the brand
 * word "AI Board" — but never as good as the real TTS-1-HD MP3s.
 *
 * speak() returns a Promise that resolves on the LAST utterance's `onend`
 * so the phase loop can await it the same way it awaits demoAudio.play().
 * No more text-out-paces-voice with fixed timers.
 */

export type Lang = 'en' | 'es' | 'pt';

const PREFERRED: Record<Lang, RegExp[]> = {
  en: [
    /Microsoft Aria.*Neural/i,
    /Microsoft Jenny.*Neural/i,
    /Microsoft Davis.*Neural/i,
    /Microsoft Guy.*Neural/i,
    /Microsoft Aria.*Online/i,
    /Google US English/i,
    /Samantha/i,
    /Alex/i,
    /Microsoft Aria/i,
  ],
  es: [
    /Microsoft Salome.*Neural/i,   // es-CO
    /Microsoft Gonzalo.*Neural/i,
    /Microsoft Salome/i,
    /Microsoft Dalia.*Neural/i,    // es-MX (neutral Latin)
    /Microsoft Jorge.*Neural/i,
    /Microsoft Dalia/i,
    /Microsoft Paloma.*Neural/i,   // es-US
    /Microsoft Elvira.*Neural/i,   // es-ES last
    /Google español/i,
  ],
  pt: [
    /Microsoft Francisca.*Neural/i,
    /Microsoft Antonio.*Neural/i,
    /Google português do Brasil/i,
  ],
};

const EXACT_LANG: Record<Lang, string[]> = {
  en: ['en-US', 'en_US'],
  es: ['es-CO', 'es_CO', 'es-MX', 'es_MX', 'es-US', 'es_US', 'es-ES', 'es_ES'],
  pt: ['pt-BR', 'pt_BR', 'pt-PT', 'pt_PT'],
};

class Voice {
  private muted = false;
  private currentLang: Lang = 'en';
  private cachedVoice: SpeechSynthesisVoice | null = null;
  private cachedEnglishVoice: SpeechSynthesisVoice | null = null;

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try { window.speechSynthesis.getVoices(); } catch {}
      window.speechSynthesis.addEventListener('voiceschanged', () => {
        this.cachedVoice = this.pickVoice(this.currentLang);
        this.cachedEnglishVoice = this.pickVoice('en');
      });
    }
  }

  setLang(lang: Lang): void {
    this.currentLang = lang;
    this.cachedVoice = this.pickVoice(lang);
    if (!this.cachedEnglishVoice) this.cachedEnglishVoice = this.pickVoice('en');
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (m) this.cancel();
  }

  isMuted(): boolean { return this.muted; }
  isSupported(): boolean { return typeof window !== 'undefined' && 'speechSynthesis' in window; }

  /** Returns a Promise that resolves when narration finishes (or is cancelled).
   *  Bilingual: switches voice for the literal phrase "AI Board". */
  speak(text: string): Promise<void> {
    if (!this.isSupported() || this.muted) return Promise.resolve();
    return new Promise<void>((resolve) => {
      try {
        window.speechSynthesis.cancel();
        const native = this.cachedVoice || this.pickVoice(this.currentLang);

        if (this.currentLang === 'en' || !/AI Board/.test(text)) {
          const u = this.makeUtter(text, native);
          u.onend = () => resolve();
          u.onerror = () => resolve();
          window.speechSynthesis.speak(u);
          return;
        }

        const parts = text.split(/(\bAI Board\b)/).filter((p) => p && p.trim());
        if (!parts.length) return resolve();
        const enVoice = this.cachedEnglishVoice || this.pickVoice('en');

        let remaining = parts.length;
        const done = () => { remaining -= 1; if (remaining <= 0) resolve(); };

        for (const part of parts) {
          const v = /^AI Board$/.test(part) ? enVoice : native;
          const u = this.makeUtter(part, v);
          u.onend = done;
          u.onerror = done;
          window.speechSynthesis.speak(u);
        }
      } catch (e) {
        console.warn('TTS speak failed', e);
        resolve();
      }
    });
  }

  private makeUtter(text: string, voice: SpeechSynthesisVoice | null): SpeechSynthesisUtterance {
    const u = new SpeechSynthesisUtterance(text);
    if (voice) { u.voice = voice; u.lang = voice.lang; }
    u.rate = 0.92;
    u.pitch = 1.00;
    u.volume = 1.0;
    return u;
  }

  pause(): void { try { window.speechSynthesis?.pause(); } catch {} }
  resume(): void { try { window.speechSynthesis?.resume(); } catch {} }
  cancel(): void { try { window.speechSynthesis?.cancel(); } catch {} }

  describe(): string {
    const v = this.cachedVoice;
    return v ? `${v.name} · ${v.lang} · ${v.localService ? 'local' : 'online'}` : '(no voice picked yet)';
  }

  describeEnglish(): string {
    const v = this.cachedEnglishVoice;
    return v ? `${v.name} · ${v.lang} · ${v.localService ? 'local' : 'online'}` : '(no en-US voice picked yet)';
  }

  listAvailable(): string[] {
    if (!this.isSupported()) return [];
    try {
      return window.speechSynthesis.getVoices()
        .filter((v) => v.lang.startsWith(this.currentLang))
        .map((v) => `${v.name} · ${v.lang} · ${v.localService ? 'local' : 'online'}`);
    } catch { return []; }
  }

  private pickVoice(lang: Lang): SpeechSynthesisVoice | null {
    if (!this.isSupported()) return null;
    try {
      const all = window.speechSynthesis.getVoices();
      if (!all.length) return null;
      const exactTags = EXACT_LANG[lang];
      for (const re of PREFERRED[lang]) {
        const hit = all.find((v) => re.test(v.name) && exactTags.some((t) => v.lang === t));
        if (hit) return hit;
      }
      for (const re of PREFERRED[lang]) {
        const hit = all.find((v) => re.test(v.name));
        if (hit) return hit;
      }
      const exact = all.filter((v) => exactTags.some((t) => v.lang === t));
      if (exact.length) return this.bestOf(exact);
      const family = all.filter((v) => v.lang.startsWith(lang + '-') || v.lang.startsWith(lang + '_'));
      if (family.length) return this.bestOf(family);
      return null;
    } catch { return null; }
  }

  private bestOf(list: SpeechSynthesisVoice[]): SpeechSynthesisVoice {
    return list.map((v) => ({ v, s: this.score(v) })).sort((a, b) => a.s - b.s)[0].v;
  }

  private score(v: SpeechSynthesisVoice): number {
    const name = v.name.toLowerCase();
    let s = 100;
    if (/neural/i.test(v.name))   s -= 70;
    if (/natural/i.test(v.name))  s -= 50;
    if (/online/i.test(v.name))   s -= 40;
    if (/wavenet/i.test(v.name))  s -= 60;
    if (/premium|enhanced/i.test(v.name)) s -= 30;
    if (/siri|samantha|alex|karen|moira|tessa/i.test(name)) s -= 25;
    if (v.localService && !/neural|natural|online/i.test(v.name)) s += 40;
    if (/espeak|festival|legacy/i.test(name)) s += 50;
    return s;
  }
}

export const voice = new Voice();

if (typeof window !== 'undefined') {
  (window as any).aiBoardDemoVoice = voice;
}
